import { Package, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useOrder } from "@/app/providers";
import { useOrderPersonalization } from "@/shared/hooks/useOrderPersonalization";

const OrderSummary: React.FC = () => {
  const { t } = useTranslation();
  const { orderData, isMergedOrder } = useOrder();
  const personalization = useOrderPersonalization();

  // Calculate shipping cost dynamically from orderData
  // At this stage, shippingCost MUST be defined. If not, it's an error in the flow.
  const getShippingCost = () => {
    // For merged orders, shipping is already paid
    if (isMergedOrder) return 0;

    // If no delivery method selected, this is an error - user should be redirected to OrderForm
    if (!orderData.deliveryMethod) {
      console.error('❌ OrderSummary: deliveryMethod is not defined');
      throw new Error('Delivery method not selected');
    }

    // If shippingCost is not defined, this is an error - user should be redirected to OrderForm
    if (orderData.shippingCost === undefined || orderData.shippingCost === null) {
      console.error('❌ OrderSummary: shippingCost is not defined for method:', orderData.deliveryMethod);
      throw new Error('Shipping cost not defined');
    }

    // Use the shipping cost from orderData (set when method was selected)
    // This comes from Sendcloud/configuration, not hardcoded values
    return orderData.shippingCost;
  };

  // Get the current product being paid for
  const getCurrentProduct = () => {
    if (!orderData.products || orderData.products.length === 0) {
      // Fallback to single product data for backward compatibility
      return {
        reference: orderData.productReference,
        model: orderData.productModel,
        amount: orderData.amount,
      };
    }

    // For merged orders, the product to pay is always the last one in the list
    if (isMergedOrder) {
      return orderData.products[orderData.products.length - 1];
    }

    // For new orders, all products should be shown (they're all being paid for)
    return null; // This will trigger the all-products display
  };

  // Calculate subtotal based on order type
  const getSubtotal = () => {
    const currentProduct = getCurrentProduct();

    // For merged orders, only calculate amount for the current product being paid for
    if (isMergedOrder && currentProduct) {
      return parseFloat(currentProduct.amount || "0");
    }

    // For new orders, calculate total from all products
    if (orderData.products && orderData.products.length > 0) {
      return orderData.products.reduce((total, product) => {
        return total + parseFloat(product.amount || "0");
      }, 0);
    } else {
      // Fallback to single amount for backward compatibility
      return parseFloat(orderData.amount || "0");
    }
  };

  // For merged orders, shipping was already paid, so don't charge again
  const shippingCost = isMergedOrder ? 0 : getShippingCost();
  const subtotal = getSubtotal();
  const total = subtotal + shippingCost;
  const currentProduct = getCurrentProduct();

  return (
    <div
      className={`border ${personalization.card.borderRadius || 'rounded-2xl'} ${personalization.card.shadow || 'shadow-md'} overflow-hidden`}
      style={{
        backgroundColor: personalization.card.background,
        borderColor: personalization.card.border,
      }}
    >
      <div className="p-4 lg:p-5 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2
            className="text-base lg:text-lg font-bold"
            style={{ color: personalization.text.heading }}
          >
            {t('orderSummary.title')}
          </h2>
        </div>

        {/* Products Section */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Package className="w-3.5 h-3.5" style={{ color: personalization.icon.primary }} />
            <h3
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: personalization.text.secondary }}
            >
              {isMergedOrder
                ? t('orderSummary.product.toPay')
                : orderData.products && orderData.products.length > 1
                  ? t('orderSummary.product.titlePlural')
                  : t('orderSummary.product.title')}
            </h3>
          </div>

          <div className="space-y-1 pl-6">
            {isMergedOrder && currentProduct ? (
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: personalization.text.primary }}>{currentProduct.reference}</p>
                </div>
                <p className="text-sm font-bold ml-3" style={{ color: personalization.text.primary }}>{parseFloat(currentProduct.amount).toFixed(2)} €</p>
              </div>
            ) : orderData.products && orderData.products.length > 0 ? (
              orderData.products.map((product, index) => (
                <div key={index} className="flex justify-between items-start">
                  <p className="text-sm font-medium" style={{ color: personalization.text.primary }}>{product.reference}</p>
                  <p className="text-sm font-bold ml-3" style={{ color: personalization.text.primary }}>{parseFloat(product.amount).toFixed(2)} €</p>
                </div>
              ))
            ) : (
              <div className="flex justify-between items-start">
                <p className="text-sm font-medium" style={{ color: personalization.text.primary }}>{orderData.productReference}</p>
                <p className="text-sm font-bold ml-3" style={{ color: personalization.text.primary }}>{subtotal.toFixed(2)} €</p>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Address Section */}
        <div className="py-3 border-y" style={{ borderColor: personalization.card.border }}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" style={{ color: personalization.icon.primary }} />
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: personalization.text.secondary }}>
                {t('orderSummary.delivery.address')}
              </h3>
            </div>
            <span className="text-xs font-bold" style={{ color: personalization.text.primary }}>
              {isMergedOrder ? t('orderSummary.delivery.included') : `${shippingCost.toFixed(2)} €`}
            </span>
          </div>
          <div className="text-[11px] pl-6 opacity-70 leading-tight" style={{ color: personalization.text.secondary }}>
            {orderData.pickupPoint ? (
              <>
                <div className="font-semibold">{orderData.pickupPoint.name}</div>
                <div>{orderData.pickupPoint.address}, {orderData.pickupPoint.postalCode} {orderData.pickupPoint.city}</div>
              </>
            ) : (
              <>{orderData.address}, {orderData.postalCode} {orderData.city}</>
            )}
          </div>
        </div>

        {/* Price Summary */}
        <div className="space-y-1 pt-1">
          <div className="flex justify-between text-xs opacity-70">
            <span>{t('orderSummary.summary.subtotal')}</span>
            <span>{subtotal.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold" style={{ color: personalization.text.heading }}>{t('orderSummary.summary.total')}</span>
            <span className="text-lg font-bold" style={{ color: personalization.text.heading }}>{total.toFixed(2)} €</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
